import React, { useEffect, useRef, useState } from "react";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import "photoswipe/style.css";

const PhotoSwipeGallery = ({ galleryHtml, galleryId = "gallery" }) => {
  const galleryRef = useRef(null);
  const lightboxRef = useRef(null);
  const [galleryItems, setGalleryItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Parse WordPress gallery HTML to extract image data
  const parseGalleryHtml = (html) => {
    if (!html) return [];

    try {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html;
      const items = [];
      const figureElements = tempDiv.querySelectorAll("figure.gallery-item");

      figureElements.forEach((figure, index) => {
        const link = figure.querySelector("a");
        const img = figure.querySelector("img");

        if (link && img) {
          const fullSizeUrl = link.getAttribute("href");
          const thumbnailUrl = img.getAttribute("src");
          const alt = img.getAttribute("alt") || `Image ${index + 1}`;

          // Get dimensions
          let width = parseInt(img.getAttribute("width")) || 1024;
          let height = parseInt(img.getAttribute("height")) || 683;
          let aspectRatio = width / height;

          // Try srcset for better dimensions
          const srcset = img.getAttribute("srcset");
          if (srcset) {
            const sources = srcset
              .split(",")
              .map((src) => {
                const parts = src.trim().split(" ");
                if (parts.length >= 2) {
                  return {
                    url: parts[0],
                    width: parseInt(parts[1].replace("w", "")),
                  };
                }
                return null;
              })
              .filter(Boolean);

            if (sources.length > 0) {
              const largest = sources.reduce((prev, current) =>
                current.width > prev.width ? current : prev,
              );
              width = largest.width;
              height = aspectRatio
                ? largest.width / aspectRatio
                : Math.round(width * 0.667);
            }
          }

          const figcaption = figure.querySelector("figcaption");
          const caption = figcaption ? figcaption.textContent.trim() : "";

          items.push({
            src: fullSizeUrl,
            thumbnail: thumbnailUrl,
            width,
            height,
            alt,
            caption,
            id: `${galleryId}-${index}`,
          });
        }
      });

      return items;
    } catch (err) {
      console.error("Error parsing gallery HTML:", err);
      return [];
    }
  };

  // Parse gallery HTML when component mounts
  useEffect(() => {
    if (galleryHtml) {
      console.log("Parsing gallery HTML...");
      const items = parseGalleryHtml(galleryHtml);
      console.log("Found", items.length, "gallery items");
      setGalleryItems(items);
      setIsLoading(false);
    }
  }, [galleryHtml]);

  // Initialize PhotoSwipe after items are loaded
  useEffect(() => {
    if (isLoading || galleryItems.length === 0 || !galleryRef.current) {
      return;
    }

    // Wait for DOM to be ready
    const timer = setTimeout(() => {
      console.log("Initializing PhotoSwipe...");

      // Clean up previous instance
      if (lightboxRef.current) {
        lightboxRef.current.destroy();
        lightboxRef.current = null;
      }

      try {
        const lightbox = new PhotoSwipeLightbox({
          gallery: `#${galleryId}`,
          children: "a",
          pswpModule: () => import("photoswipe"),
          showHideAnimationType: "zoom",
          bgOpacity: 0.85,
          spacing: 0.1,
          allowPanToNext: true,
          loop: true,
          wheelToZoom: true,
          pinchToClose: true,
          closeOnVerticalDrag: true,
          /* padding: { top: 20, bottom: 40, left: 100, right: 100 }, */
          // Enable built-in navigation
          arrowKeys: true,
          returnFocus: true,
          trapFocus: true,
          // Ensure UI elements are visible
          appendToEl: document.body,
        });

        lightbox.on("beforeOpen", () => {
          console.log("PhotoSwipe opening");
          document.body.style.overflow = "hidden";
        });

        lightbox.on("destroy", () => {
          document.body.style.overflow = "";
        });

        // Fix aria-hidden and ensure arrows are visible
        lightbox.on("openSlide", () => {
          // Force arrow buttons to be visible and interactive
          setTimeout(() => {
            const prevButton = document.querySelector(
              ".pswp__button--arrow--prev",
            );
            const nextButton = document.querySelector(
              ".pswp__button--arrow--next",
            );

            // Check if mobile
            const isMobile = window.innerWidth <= 768;

            if (prevButton) {
              prevButton.removeAttribute("aria-hidden");
              prevButton.style.display = "flex";
              prevButton.style.visibility = "visible";
              prevButton.style.opacity = "0.8";
              prevButton.style.position = "fixed";
              prevButton.style.zIndex = "1075";
              prevButton.style.alignItems = "center";
              prevButton.style.justifyContent = "center";

              if (isMobile) {
                // Mobile: position below image
                prevButton.style.top = "auto";
                prevButton.style.bottom = "80px";
                prevButton.style.left = "50%";
                prevButton.style.transform =
                  "translateX(-50%) translateX(-30px)";
              } else {
                // Desktop: position on left side
                prevButton.style.top = "50%";
                prevButton.style.left = "30px";
                prevButton.style.transform = "translateY(-50%)";
              }
            }

            if (nextButton) {
              nextButton.removeAttribute("aria-hidden");
              nextButton.style.display = "flex";
              nextButton.style.visibility = "visible";
              nextButton.style.opacity = "0.8";
              nextButton.style.position = "fixed";
              nextButton.style.zIndex = "1075";
              nextButton.style.alignItems = "center";
              nextButton.style.justifyContent = "center";

              if (isMobile) {
                // Mobile: position below image
                nextButton.style.top = "auto";
                nextButton.style.bottom = "80px";
                nextButton.style.left = "50%";
                nextButton.style.transform =
                  "translateX(-50%) translateX(30px)";
                nextButton.style.right = "auto";
              } else {
                // Desktop: position on right side
                nextButton.style.top = "50%";
                nextButton.style.right = "30px";
                nextButton.style.transform = "translateY(-50%)";
                nextButton.style.left = "auto";
              }
            }

            console.log(
              "Arrow buttons positioned for",
              isMobile ? "mobile" : "desktop",
              {
                prevButton,
                nextButton,
              },
            );
          }, 100);
        });

        lightbox.init();
        lightboxRef.current = lightbox;
        console.log("PhotoSwipe initialized successfully");
      } catch (err) {
        console.error("PhotoSwipe initialization error:", err);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      if (lightboxRef.current) {
        lightboxRef.current.destroy();
        lightboxRef.current = null;
      }
      document.body.style.overflow = "";
    };
  }, [isLoading, galleryItems, galleryId]);

  if (isLoading) {
    return <div className="photoswipe-gallery-loading">Loading gallery...</div>;
  }

  if (galleryItems.length === 0) {
    return <div>No gallery items found.</div>;
  }

  return (
    <div ref={galleryRef} id={galleryId} className="photoswipe-gallery">
      {galleryItems.map((item, index) => (
        <a
          key={item.id}
          href={item.src}
          data-pswp-width={item.width}
          data-pswp-height={item.height}
          className="gallery-item-link"
          title={item.alt}
        >
          <img src={item.thumbnail} alt={item.alt} loading="lazy" />
          {item.caption && (
            <div className="gallery-item-caption">{item.caption}</div>
          )}
        </a>
      ))}
    </div>
  );
};

export default PhotoSwipeGallery;
